import React, { createContext, useContext } from "react";
import { getData, patchData, postData } from "../utilities/postData";
import { useDualMsal } from "./MsalContext";

const DataProviderContext = createContext();

export const useData = () => {
  return useContext(DataProviderContext);
};

export function DataProvider({ children }) {
  const { currentUser, getToken, isAuthenticated } = useDualMsal();
  const getUserState = async (userId) => {
    const token = await getToken();
    return getData({
      endpoint: "user" + (userId ? "/" + userId : ""),
      accessToken: token,
    });
  };

  const getUsersStatuses = async () => {
    const token = await getToken();
    return getData({
      endpoint: "users",
      accessToken: token,
    });
  };

  const createInvite = async (data) => {
    const token = await getToken();
    return postData({
      timeout: 30000,
      endpoint: "create_invite",
      accessToken: token,
      data: data,
    });
  };

  const checkRelmedId = async (relmedId) => {
    const token = await getToken();
    return postData({
      timeout: 8000,
      endpoint: "check_relmed_id",
      accessToken: token,
      data: { relmedId },
    });
  };

  const setUserState = async (u) => {
    const suffix = u.id && u.id !== currentUser?.oid ? "/" + u.id : "";
    const token = await getToken();
    return patchData({
      endpoint: "user" + suffix,
      accessToken: token,
      data: u,
    });
  };

  const postTaskData = async (data) => {
    const token = await getToken();
    // Add participantId to the call for caching purposes
    return postData({
      endpoint: "post-data",
      accessToken: token,
      participantId: currentUser?.oid, // Pass the current user's OID as participantId
      data: data,
    });
  };

  const resendInvite = async (userId, isPasswordReset = false) => {
    const token = await getToken();
    return postData({
      endpoint: "resend_invite",
      accessToken: token,
      data: { userId, isPasswordReset },
    });
  };

  // Update getSubmissionMetrics to accept an optional site parameter
  const getSubmissionMetrics = async (site = null) => {
    const token = await getToken();
    const params = {};
    if (site && site !== "All Sites") {
      params.site = site; // Add site to parameters if it's not "All Sites"
    }
    return getData({
      endpoint: "submission-metrics",
      accessToken: token,
      parameters: params, // Pass parameters to getData
    });
  };

  const retrySubmission = async (relmedId) => {
    const token = await getToken();
    return postData({
      endpoint: "retry-submission",
      accessToken: token,
      data: { relmedId },
    });
  };

  // initializeRegistration function removed as it's no longer needed.
  // Validation happens in ParticipantRegister.js via API call now.

  const completeRegistration = async (newUserId, account) => {
    // Read the raw invite token from localStorage
    const inviteToken = localStorage.getItem("invite_token");

    if (!inviteToken) {
      console.error(
        "Cannot complete registration: missing invite token in localStorage."
      );
      // Optionally clear registration_data if invite token is missing unexpectedly
      localStorage.removeItem("registration_data");
      return false;
    }

    if (!newUserId) {
      console.error(
        "Cannot complete registration: missing new user ID (B2C OID)."
      );
      // Clear potentially stale data
      localStorage.removeItem("registration_data");
      localStorage.removeItem("invite_token");
      return false;
    }

    const b2cAuthToken = await getToken(); // Get the current B2C auth token

    if (!b2cAuthToken) {
      console.error(
        "Cannot complete registration: failed to get B2C authentication token."
      );
      // Don't clear localStorage here, user might just need to re-auth
      return false;
    }

    // Prepare data for the backend, including the invite token
    const finalizeData = {
      invite_token: inviteToken, // Send the raw invite token
      new_user_id: newUserId, // Send the B2C Object ID
    };

    // Add optional details if available from the B2C account object
    if (account.name) {
      finalizeData.new_user_name = account.name;
    }

    if (account.username) {
      finalizeData.new_user_email = account.username;
    }
    // Add the policy claim if available in the account object
    if (account.policy) {
      finalizeData.policy = account.policy;
    }

    // Call the backend endpoint
    return postData({
      endpoint: "b2c-signup-complete",
      accessToken: b2cAuthToken, // Use the B2C auth token for the API call
      data: finalizeData,
    })
      .then((response) => {
        // Cleanup happens in Participant.js now after successful call
        console.log("Backend registration completion successful.");
        return true; // Indicate success to Participant.js
      })
      .catch((error) => {
        console.error("Backend registration completion failed:", error);
        // Do not automatically clean up localStorage here, let Participant.js handle it based on success/failure
        // Check for specific errors if needed, e.g., token expired error from backend
        // if (error.status === 410) { ... }
        return false; // Indicate failure
      });
  };

  // Validate participant invitation token (added function)
  const validateInviteToken = async (token) => {
    if (!token) {
      throw new Error("No token provided for validation.");
    }
    // Use postData utility - assumes backend function is mapped to 'validate_invite_token' endpoint
    // No access token needed as the backend function has authLevel: 'anonymous'
    return postData({
      endpoint: "validate_invite_token",
      data: { token }, // Send token in the request body
      // No accessToken needed here
    });
    // The postData utility will handle the fetch and error checking
    // It will return the jsonBody on success or throw an error on failure
  };

  // Create invitation from expression of interest
  const createInviteFromExpression = async (
    expressionId,
    relmedId,
    displayName
  ) => {
    const token = await getToken();
    return postData({
      timeout: 30000,
      endpoint: "create_invite_from_expression",
      accessToken: token,
      data: {
        expressionId,
        relmedId,
        displayName,
      },
    });
  };

  // Get rewards data (balance and history) for a specific user or the current user
  const getRewardsBalance = async (targetUserId = null) => {
    const userIdToFetch = targetUserId || currentUser?.oid; // Use target ID or fallback to current user

    if (!userIdToFetch) {
      console.error(
        "Cannot get rewards: No user ID specified or user not authenticated."
      );
      // Return a default structure or throw error? Returning default for now.
      return { balance: 0, redeemedVouchers: [] };
    }
    const token = await getToken();
    if (!token) {
      console.error("Cannot get rewards: failed to get token.");
      return { balance: 0, redeemedVouchers: [] };
    }
    try {
      // Use the route: user/{userId}/rewards
      const response = await getData({
        endpoint: `user/${userIdToFetch}/rewards`, // Use the determined user ID
        accessToken: token,
      });
      // Return the full rewards object or a default if null/undefined
      return response || { balance: 0, redeemedVouchers: [] };
    } catch (error) {
      console.error(`Error fetching rewards for user ${userIdToFetch}:`, error);
      // Handle specific errors like 404 if needed, otherwise return default
      if (error.code === 404 || error.status === 404) {
        // 404 likely means no rewards doc exists yet, return default
        return { balance: 0, redeemedVouchers: [] };
      }
      // For other errors, maybe rethrow or return default? Returning default.
      return { balance: 0, redeemedVouchers: [] };
    }
  };

  // Redeem rewards for the current user
  const redeemRewards = async () => {
    if (!currentUser?.oid) {
      console.error("Cannot redeem rewards: user not authenticated.");
      throw new Error("User not authenticated."); // Throw error to be caught by caller
    }
    const token = await getToken();
    if (!token) {
      console.error("Cannot redeem rewards: failed to get token.");
      throw new Error("Failed to get authentication token.");
    }
    try {
      // Call the POST endpoint for redeeming rewards
      // The backend will read the current balance and call the payment API
      const response = await postData({
        endpoint: `redeem-rewards`, // Assuming this is the correct endpoint name
        accessToken: token,
        data: {}, // No specific data needed in body, backend reads balance
      });
      // Return the response which should contain success message and voucher details
      return response;
    } catch (error) {
      console.error("Error redeeming rewards:", error);
      // Rethrow the error so the calling component can handle it (e.g., show message)
      throw error;
    }
  };

  // Adjust reward balance for a specific user (Admin/PI only)
  const adjustRewardBalance = async (
    targetUserId,
    adjustmentAmountPence,
    reason
  ) => {
    if (!targetUserId || typeof adjustmentAmountPence !== "number" || !reason) {
      console.error("Invalid parameters for adjustRewardBalance");
      throw new Error("Invalid parameters provided for reward adjustment.");
    }
    const token = await getToken(); // Assumes the calling user (Admin/PI) is authenticated
    if (!token) {
      console.error("Cannot adjust rewards: failed to get admin token.");
      throw new Error("Failed to get authentication token.");
    }
    try {
      // Call the POST endpoint: user/{targetUserId}/adjust-reward
      const response = await postData({
        endpoint: `user/${targetUserId}/adjust-reward`,
        accessToken: token,
        data: {
          adjustmentAmount: adjustmentAmountPence, // Send amount in pence
          reason: reason,
        },
      });
      return response; // Return the response (e.g., { message: '...', newBalance: ... })
    } catch (error) {
      console.error(`Error adjusting rewards for user ${targetUserId}:`, error);
      throw error; // Rethrow error for the component to handle
    }
  };

  // Add a bonus reward for a specific task/session
  const addBonusReward = async (sessionCode, taskParam, bonusAmountPence) => {
    if (!currentUser?.oid) {
      console.error("Cannot add bonus reward: user not authenticated.");
      // Don't throw error here, just log and return to avoid interrupting task
      return;
    }
    if (
      !sessionCode ||
      !taskParam ||
      !bonusAmountPence ||
      bonusAmountPence <= 0
    ) {
      console.error("Invalid parameters for addBonusReward", {
        sessionCode,
        taskParam,
        bonusAmountPence,
      });
      return; // Log and exit gracefully
    }
    const token = await getToken();
    if (!token) {
      console.error("Cannot add bonus reward: failed to get token.");
      return;
    }
    try {
      // Call the POST endpoint
      await postData({
        endpoint: `add-reward-bonus`,
        accessToken: token,
        data: {
          sessionCode: sessionCode,
          taskParam: taskParam,
          bonusAmount: bonusAmountPence,
        },
      });
      // Success is handled by the backend (even if already awarded)
      console.log(`Bonus reward reported for ${sessionCode}:${taskParam}`);
    } catch (error) {
      // Log error but don't throw, to avoid interrupting the task iframe
      console.error(
        `Error reporting bonus reward for ${sessionCode}:${taskParam}:`,
        error
      );
    }
  };

  // Get all PO references (PI only)
  const getPOReferences = async () => {
    const token = await getToken();
    return getData({
      endpoint: "purchase-order-references",
      accessToken: token,
    });
  };

  // Get current active PO reference (PI only)
  const getCurrentPOReference = async () => {
    const token = await getToken();
    return getData({
      endpoint: "purchase-order-references/current",
      accessToken: token,
    });
  };

  // Create new PO reference (PI only)
  const createPOReference = async (purchaseOrderReference, description) => {
    const token = await getToken();
    return postData({
      endpoint: "purchase-order-references",
      accessToken: token,
      data: {
        purchaseOrderReference,
        description,
      },
    });
  };

  // Update current PO reference (PI only)
  const updatePOReference = async (purchaseOrderReference, description) => {
    const token = await getToken();
    return postData({
      endpoint: "purchase-order-references",
      accessToken: token,
      data: {
        purchaseOrderReference,
        description,
      },
    });
  };

  return (
    <DataProviderContext.Provider
      value={{
        currentUser,
        getUserState,
        setUserState,
        getUsersStatuses,
        createInvite,
        checkRelmedId,
        resendInvite,
        postTaskData,
        getSubmissionMetrics,
        retrySubmission,
        isAuthenticated,
        // initializeRegistration removed
        completeRegistration,
        validateInviteToken, // Expose the new function
        getRewardsBalance,
        redeemRewards,
        adjustRewardBalance,
        addBonusReward, // Add addBonusReward to context value
        getPOReferences,
        getCurrentPOReference,
        createPOReference,
        updatePOReference,
      }}
    >
      {children}
    </DataProviderContext.Provider>
  );
}
